import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    HomeOutlined,
    UserOutlined,
    FileTextOutlined,
    CalendarOutlined,
    SettingOutlined
} from "@ant-design/icons";
import { Spin } from "antd";
import './AiChatSummaryList.scss';
import { useGetChatSummaryLogsQuery } from "../../utils/apiSlice";
import { useEffectiveUserData } from "../../hooks/useEffectiveUserData";

const AiChatSummaryLogs = () => {
    const navigate = useNavigate();
    const { sessionId } = useParams<{ sessionId: string }>();

    const userData = useEffectiveUserData();
    const userId = userData && (userData.userId || userData.patientId) ? String(userData.userId || userData.patientId) : "";

    const { data: historyResponse, isLoading, isError } = useGetChatSummaryLogsQuery(userId, {
        skip: !userId,
    });

    const currentSession = useMemo(() => {
        if (!historyResponse) return null;
        const r = historyResponse as any;
        const list = r.sessions || (r.data && r.data.sessions) || [];
        if (!Array.isArray(list) || !list.length) return (r.session || (r.data && r.data.session) || r);
        if (sessionId) {
            const found = list.find((s: any) => String(s.session_id || s.sessionId || s.id) === String(sessionId));
            return found || list[0];
        }
        return list[0];
    }, [historyResponse, sessionId]);

    const sessionInfo = useMemo(() => {
        if (!currentSession) return null;
        const session = currentSession;
        const dateRaw = (session && (session.last_updated || session.lastUpdated || session.generated_at || session.dateTime || session.date || session.createdAt)) || "";
        const firstInteraction = (session && (session.first_interaction || session.firstInteraction)) || "";
        const lastInteraction = (session && (session.last_interaction || session.lastInteraction || dateRaw)) || "";
        return {
            date: dateRaw,
            firstInteraction,
            lastInteraction,
            patientName: (session && (session.patientName || session.patient_name || session.patient)) || "",
            problemStatement: (session && (session.problem_statement || session.problemStatement || session.summary)) || "",
            carePlan: (session && (session.carePlan || session.care_plan || session.status)) || "AI-Generated Care Plan Provided",
        };
    }, [currentSession]);

    const messages = useMemo(() => {
        if (!currentSession) return [];
        const session = currentSession as any;
        const raw = session.messages || [];
        if (Array.isArray(raw) && raw.length) {
            return raw.map((m: any, i: number) => ({
                key: m.id != null ? m.id : i,
                role: m.role || m.sender || "user",
                content: m.content || m.message || m.text || "",
                time: m.timestamp || m.time || m.createdAt || "",
            }));
        }
        return [];
    }, [currentSession]);

    const summaryBody = useMemo(() => {
        if (!currentSession) return "";
        return (currentSession as any).summary || "";
    }, [currentSession]);

    if (!userId) {
        return (
            <div className="content-wrapper">
                <div className="container-full table-padding">
                    <div className="page-header">
                        <div className="breadcrumb">
                            <h3 className="page-title">AI Chat Summary</h3>
                            <span className="customize-breadcrumb">
                                <span className="home-icon" onClick={() => navigate("/dashboard")}><HomeOutlined /></span>
                                <span className="separator"> - </span>
                                <span className="current">Full Chat Logs</span>
                            </span>
                        </div>
                    </div>
                    <div style={{ padding: "40px", textAlign: "center", color: "#888" }}>Please log in to view chat history.</div>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="content-wrapper">
                <div className="container-full table-padding">
                    <div className="page-header">
                        <div className="breadcrumb">
                            <h3 className="page-title">AI Chat Summary</h3>
                            <span className="customize-breadcrumb">
                                <span className="home-icon" onClick={() => navigate("/dashboard")}><HomeOutlined /></span>
                                <span className="separator"> - </span>
                                <span className="current">Full Chat Logs</span>
                            </span>
                        </div>
                    </div>
                    <div style={{ padding: "40px", textAlign: "center" }}><Spin tip="Loading chat history..." /></div>
                </div>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="content-wrapper">
                <div className="container-full table-padding">
                    <div className="page-header">
                        <div className="breadcrumb">
                            <h3 className="page-title">AI Chat Summary</h3>
                            <span className="customize-breadcrumb">
                                <span className="home-icon" onClick={() => navigate("/dashboard")}><HomeOutlined /></span>
                                <span className="separator"> - </span>
                                <span className="current">Full Chat Logs</span>
                            </span>
                        </div>
                    </div>
                    <div style={{ padding: "40px", textAlign: "center", color: "#ff4d4f" }}>Failed to load chat history.</div>
                </div>
            </div>
        );
    }

    const formatDate = (iso: string) =>
        iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";
    const dateLabel = formatDate(sessionInfo && sessionInfo.date ? sessionInfo.date : "");
    const firstLabel = sessionInfo && sessionInfo.firstInteraction ? formatDate(sessionInfo.firstInteraction) : "—";
    const lastLabel = sessionInfo && sessionInfo.lastInteraction ? formatDate(sessionInfo.lastInteraction) : "—";
    const timeLabel = sessionInfo && sessionInfo.date ? new Date(sessionInfo.date).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "—";
    const hasMessages = messages.length > 0;
    const hasSummaryBody = summaryBody && summaryBody.trim().length > 0;

    return (
        <div className="content-wrapper">
            <div className="container-full table-padding">
                <div className="page-header">
                    <div className="breadcrumb">
                        <h3 className="page-title">AI Chat Summary</h3>
                        <span className="customize-breadcrumb">
                            <span className="home-icon" onClick={() => navigate("/dashboard")}><HomeOutlined /></span>
                            <span className="separator"> - </span>
                            <span className="current">Full Chat Logs</span>
                        </span>
                    </div>
                </div>

                <div className="family-card1">
                    <div className="family-header1">
                        <div className="left-side1">
                            <p className="title1">{dateLabel} | Full Chat Detail</p>
                        </div>
                    </div>
                </div>

                <div className="row " style={{ border: "1px solid #EFEFFD ", marginLeft: '0.0%', marginRight: '0.0%' }}>
                    <div className="col-md-2 chat-detail-sidebar" style={{ borderRight: '1px solid rgb(239, 239, 253)' }}>
                        <div className="chat-info pt-4">
                            <div className="chat-info-fields">
                                <CalendarOutlined className="me-2" style={{ color: '#5156be', fontSize: '18px' }} />
                                {dateLabel}
                            </div>
                            {firstLabel !== "—" && (
                                <div className="chat-info-fields">
                                    <CalendarOutlined className="me-2" style={{ color: '#5156be', fontSize: '16px' }} />
                                    <span style={{ fontSize: '12px' }}>First: {firstLabel}</span>
                                </div>
                            )}
                            {lastLabel !== "—" && (
                                <div className="chat-info-fields">
                                    <CalendarOutlined className="me-2" style={{ color: '#5156be', fontSize: '16px' }} />
                                    <span style={{ fontSize: '12px' }}>Last: {lastLabel}</span>
                                </div>
                            )}
                            <div className="chat-info-fields">
                                <UserOutlined className="me-2" style={{ color: '#5156be', fontSize: '18px' }} />
                                Patient: {sessionInfo && sessionInfo.patientName ? sessionInfo.patientName : "—"}
                            </div>
                            <div className="chat-info-fields">
                                <FileTextOutlined className="me-2" style={{ color: '#5156be', fontSize: '18px' }} />
                                <span style={{ fontSize: '13px' }}>{sessionInfo && sessionInfo.problemStatement ? sessionInfo.problemStatement : "—"}</span>
                            </div>
                            <div className="chat-info-fields">
                                <SettingOutlined className="me-2" style={{ color: '#5156be', fontSize: '18px' }} />
                                {sessionInfo && sessionInfo.carePlan ? sessionInfo.carePlan : "AI-Generated Care Plan Provided"}
                            </div>
                        </div>
                    </div>
                    <div className="col-md-10 ">
                        <div className="chat-log">Full Chat Logs</div>
                        {hasMessages && messages.map((msg) => (
                            <div
                                key={msg.key}
                                className={"chat-history " + (msg.role === "assistant" || msg.role === "ai" ? "reciever" : "sender")}
                                style={msg.role === "assistant" || msg.role === "ai" ? {} : { marginLeft: "1rem" }}
                            >
                                <h6 className={msg.role === "assistant" || msg.role === "ai" ? "reciever" : "sender"}>
                                    ({(msg.role === "assistant" || msg.role === "ai") ? "AI" : "User"}, {msg.time ? new Date(msg.time).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : timeLabel})
                                </h6>
                                <p>{msg.content}</p>
                            </div>
                        ))}
                        {!hasMessages && hasSummaryBody && (
                            <div className="chat-detail-summary-body">
                                {summaryBody.split(/\n/g).map((line: string, i: number) => {
                                    const isHeading = /^#{1,6}\s/.test(line);
                                    const match = line.match(/^(#{1,6})\s/);
                                    const level = Math.min(match && match[1] ? match[1].length : 1, 6);
                                    const text = line.replace(/^#{1,6}\s/, "").trim();
                                    if (!text) return <br key={i} />;
                                    if (isHeading) {
                                        const headingStyle = { marginTop: level <= 1 ? 0 : 12, marginBottom: 6, color: "#1890ff", fontWeight: 600 };
                                        if (level === 1) return <h1 key={i} style={headingStyle}>{text}</h1>;
                                        if (level === 2) return <h2 key={i} style={headingStyle}>{text}</h2>;
                                        if (level === 3) return <h3 key={i} style={headingStyle}>{text}</h3>;
                                        if (level === 4) return <h4 key={i} style={headingStyle}>{text}</h4>;
                                        if (level === 5) return <h5 key={i} style={headingStyle}>{text}</h5>;
                                        return <h6 key={i} style={headingStyle}>{text}</h6>;
                                    }
                                    return <p key={i} style={{ margin: "4px 0" }}>{line}</p>;
                                })}
                            </div>
                        )}
                        {!hasMessages && !hasSummaryBody && (
                            <div style={{ padding: "24px", color: "#888" }}>No messages or summary in this session.</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AiChatSummaryLogs;
